/**
 * @fileoverview Store-day boundary maths.
 *
 * CANONICAL COPY. Extracted from backend/src/utils/storeTime.js and parameterised
 * by timezone so the till and the cloud compute identical boundaries.
 *
 * This is the highest-stakes module in `shared/`. Every reporting window in both
 * products is a *store* day, not a UTC day, and the two only coincide in winter.
 * If the till and the cloud disagree about where a day starts, they report
 * different totals for the same day and both look correct — the worst kind of
 * defect this system can have.
 *
 * The till binds one timezone at startup from its own configuration. The cloud
 * binds a different one per request, because stores differ; never assume London.
 */

/**
 * Builds the boundary helpers for one IANA timezone.
 *
 * @param {string} timeZone IANA name, e.g. 'Europe/London'.
 */
function createStoreTime(timeZone) {
  if (!timeZone) {
    throw new Error('createStoreTime requires an IANA timezone name.');
  }

  const partsFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  // What the store clock reads at the given instant.
  function wallClockParts(instant) {
    const parts = {};
    for (const { type, value } of partsFormatter.formatToParts(instant)) {
      if (type !== 'literal') parts[type] = Number(value);
    }
    return parts;
  }

  // How far ahead of UTC the store is at the given instant, in milliseconds.
  function offsetAt(instant) {
    const p = wallClockParts(instant);
    const asIfUTC = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    return asIfUTC - Math.floor(instant.getTime() / 1000) * 1000;
  }

  // Turn a store wall-clock reading into the absolute instant it refers to.
  // `month` is 0-based and values may overflow (day 0, day 32, month 12) exactly as Date.UTC allows.
  function fromWallClock(year, month, day, hour = 0, minute = 0, second = 0, ms = 0) {
    const naive = Date.UTC(year, month, day, hour, minute, second, ms);
    // The offset we need depends on the instant we are solving for, so apply a first
    // estimate then re-measure - one correction settles every DST transition.
    const firstPass = naive - offsetAt(new Date(naive));
    return new Date(naive - offsetAt(new Date(firstPass)));
  }

  // Calendar fields of the store day containing `instant` (month 0-based, for symmetry with Date).
  function storeDate(instant = new Date()) {
    const p = wallClockParts(instant);
    return {
      year: p.year,
      month: p.month - 1,
      day: p.day,
      hour: p.hour,
      minute: p.minute,
      second: p.second,
    };
  }

  function startOfDay(instant = new Date(), dayShift = 0) {
    const { year, month, day } = storeDate(instant);
    return fromWallClock(year, month, day + dayShift);
  }

  function endOfDay(instant = new Date(), dayShift = 0) {
    const { year, month, day } = storeDate(instant);
    return fromWallClock(year, month, day + dayShift, 23, 59, 59, 999);
  }

  // Weeks run Monday to Sunday.
  function startOfWeek(instant = new Date(), weekShift = 0) {
    const { year, month, day } = storeDate(instant);
    const weekday = new Date(Date.UTC(year, month, day)).getUTCDay(); // 0 = Sunday
    const daysToMonday = weekday === 0 ? 6 : weekday - 1;
    return fromWallClock(year, month, day - daysToMonday + weekShift * 7);
  }

  function endOfWeek(instant = new Date(), weekShift = 0) {
    const { year, month, day } = storeDate(startOfWeek(instant, weekShift));
    return fromWallClock(year, month, day + 6, 23, 59, 59, 999);
  }

  function startOfMonth(instant = new Date(), monthShift = 0) {
    const { year, month } = storeDate(instant);
    return fromWallClock(year, month + monthShift, 1);
  }

  function endOfMonth(instant = new Date(), monthShift = 0) {
    const { year, month } = storeDate(instant);
    // Day 0 of the following month is the last day of this one.
    return fromWallClock(year, month + monthShift + 1, 0, 23, 59, 59, 999);
  }

  // The same wall-clock time as `instant`, on an earlier or later store day.
  function sameTimeOnDay(instant = new Date(), dayShift = 0) {
    const { year, month, day, hour, minute, second } = storeDate(instant);
    return fromWallClock(year, month, day + dayShift, hour, minute, second);
  }

  // YYYY-MM-DD as the store would write it
  function toDateString(instant = new Date()) {
    const { year, month, day } = storeDate(instant);
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  // Bounds of a YYYY-MM-DD supplied by the client, read as a store day.
  function startOfDateString(dateString) {
    const [year, month, day] = dateString.split('-').map(Number);
    return fromWallClock(year, month - 1, day);
  }

  function endOfDateString(dateString) {
    const [year, month, day] = dateString.split('-').map(Number);
    return fromWallClock(year, month - 1, day, 23, 59, 59, 999);
  }

  // Shift a YYYY-MM-DD by whole days without leaving the store calendar.
  function shiftDateString(dateString, dayShift) {
    const [year, month, day] = dateString.split('-').map(Number);
    return toDateString(fromWallClock(year, month - 1, day + dayShift));
  }

  return {
    timeZone,
    storeDate,
    startOfDay,
    endOfDay,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    sameTimeOnDay,
    toDateString,
    startOfDateString,
    endOfDateString,
    shiftDateString,
  };
}

module.exports = { createStoreTime };
